<template>
  <VModal :showing="showing" container-classes="w-192" @update:showing="$emit('close')">
    <template #header>
      <h2 class="mb-0 text-lg font-medium text-gray-900">Images</h2>
      <p class="mb-0 text-sm text-gray-700">Choose an existing file from the shelf.</p>
    </template>

    <template #default>
      <VSearchableTable
        v-model:search="query"
        class="deck-selector"
        :auto-load="true"
        :options="results as ShelfFile[]"
        :current-item="selectedFile"
        :loading="loading"
        :count="count"
        placeholder="Search decks"
        data-testid="deck-selector-table"
        layout="fixed"
        @selected="selectFile"
        @load-more="scroll"
      >
        <template #current="{ item }">
          <div>{{ (item as ShelfFile).name }}</div>
        </template>

        <template #headers>
          <th class="pl-6">Thumbnail</th>
          <th @click="sortOn('name')">
            Name
            <FontAwesomeIcon v-if="sortKey === 'name'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
          <th class="pr-6 text-right" @click="sortOn('size')">
            Size
            <FontAwesomeIcon v-if="sortKey === 'size'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
        </template>

        <template #option="{ item }">
          <td class="w-20 pl-6"><img class="w-auto h-12 rounded-md" :src="(item as ShelfFile).thumbnail as string" /></td>
          <td>{{ (item as ShelfFile).name }}</td>
          <td class="w-48 pr-6 text-sm font-normal text-right whitespace-nowrap">
            {{ formatDateTimeMed((item as ShelfFile).updated_at as string) }}
          </td>
        </template>
      </VSearchableTable>
    </template>

    <template #footer>
      <slot name="footer">
        <div class="flex justify-end gap-2">
          <VButton @click="$emit('close')">Cancel</VButton>
        </div>
      </slot>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { ref, computed } from 'vue'

import type { ShelfFile } from '~/entities/shelfFile'
import type { QueryParameters } from '@/composables/useIndexSearch'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'

interface ApiQuery {
  sort_by: `${SceneQuery['sortDir']}${SceneQuery['sortKey']}`
  query: string | null
  limit: number
}

interface SceneQuery extends QueryParameters {
  sortKey: 'name' | 'size'
  sortDir: '-' | ''
}

const props = defineProps({
  showing: {
    type: Boolean,
    default: false,
  },
  companyId: {
    type: Number,
    required: true,
  },
  folderId: {
    type: [Number, Object] as PropType<number | null>,
    default: null,
  },
})

const emit = defineEmits(['update:showing', 'on-file-selected', 'close'])

const { continuation, results, query, count, page, limit, loading, sortKey, sortDir } = useIndexSearch<ShelfFile, SceneQuery, ApiQuery>({
  initParams: { limit: 10 },
  endpoint: () => (props.showing ? `/v2/companies/${props.companyId}/shelf/files` : undefined),
  finalParameters: ({ query, limit, sortKey, sortDir }) => {
    return {
      sort_by: `${sortDir}${sortKey}`,
      folder_id: props.folderId,
      query,
      limit,
    }
  },
})

const files = computed(() => results.value)
const selectedFile = ref<null | any>(null)

function sortOn(key: SceneQuery['sortKey']) {
  if (key === sortKey.value) {
    sortDir.value = sortDir.value === '' ? '-' : ''
    return
  }
  sortKey.value = key
  sortDir.value = ''
}

function selectFile(file: File) {
  selectedFile.value = file
  emit('on-file-selected', file)
}

async function scroll() {
  if (!loading.value && count.value > files.value.length) {
    continuation.value++
  }
}
</script>

<style lang="stylus" scoped>
.v-searchable-table
  & :deep(th)
    @apply text-xs uppercase text-gray-700 bg-gray-50 px-4 py-3 font-medium border-l-0 cursor-pointer
  & :deep(td)
    @apply text-gray-800 p-4 border-0
  & :deep(tbody tr:nth-child(even) td)
    @apply bg-gray-50
  & :deep(tbody tr:hover td)
    @apply bg-gray-200
</style>
